import { useState } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const inputCls =
  "h-9 border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400 focus-visible:ring-blue-600";

export const TextField = ({ label, value, onChange, placeholder, type = "text", testid, required, className, hint }) => (
  <div className={cn("space-y-1.5", className)}>
    {label && (
      <Label className="label-xs">
        {label}
        {required && <span className="ml-1 text-rose-600">*</span>}
      </Label>
    )}
    <Input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className={inputCls}
    />
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

export const TextAreaField = ({ label, value, onChange, placeholder, rows = 4, testid, className }) => (
  <div className={cn("space-y-1.5", className)}>
    {label && <Label className="label-xs">{label}</Label>}
    <Textarea
      value={value ?? ""}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400 focus-visible:ring-blue-600"
    />
  </div>
);

export const SelectField = ({ label, value, onChange, options, placeholder = "Select", testid, className, allowAll }) => (
  <div className={cn("space-y-1.5", className)}>
    {label && <Label className="label-xs">{label}</Label>}
    <Select value={value ?? undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className={inputCls}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72 border-slate-200 bg-white text-slate-800">
        {allowAll && <SelectItem value="all">All</SelectItem>}
        {options.map((o) => {
          const val = typeof o === "string" ? o : o.value;
          const lab = typeof o === "string" ? o : o.label;
          return (
            <SelectItem key={val} value={val} className="text-sm">
              {lab}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  </div>
);

export const TagsField = ({ label, values = [], onChange, placeholder = "Type and press Enter", testid, suggestions = [] }) => {
  const [draft, setDraft] = useState("");
  const add = (v) => {
    const t = (v || draft).trim();
    if (t && !values.includes(t)) onChange([...values, t]);
    setDraft("");
  };
  const matches = draft.length > 0
    ? suggestions.filter((s) => s.toLowerCase().includes(draft.toLowerCase()) && !values.includes(s)).slice(0, 6)
    : [];
  return (
    <div className="space-y-1.5">
      {label && <Label className="label-xs">{label}</Label>}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
        <div className="flex flex-wrap gap-1.5">
          {values.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
              {v}
              <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} aria-label={`Remove ${v}`}>
                <X className="h-3 w-3 text-slate-500 hover:text-rose-600" />
              </button>
            </span>
          ))}
          <input
            value={draft}
            data-testid={testid}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                add();
              }
              if (e.key === "Backspace" && !draft && values.length) onChange(values.slice(0, -1));
            }}
            placeholder={placeholder}
            className="min-w-[140px] flex-1 bg-transparent px-1 text-sm text-slate-800 outline-none placeholder:text-slate-400"
          />
        </div>
      </div>
      {matches.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {matches.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => add(m)}
              className="rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700 hover:bg-blue-100"
            >
              + {m}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const CreatableSelectField = ({ label, value, onChange, options, placeholder = "Select", testid, className, addLabel = "Add new…", onCreate }) => {
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");
  const list = options.map((o) => (typeof o === "string" ? o : o.value));

  const commit = async () => {
    const v = draft.trim();
    if (!v) return;
    const created = await onCreate?.(v);
    if (created === false) return;
    onChange(v);
    setDraft("");
    setCreating(false);
  };

  if (creating)
    return (
      <div className={cn("space-y-1.5", className)}>
        {label && <Label className="label-xs">{label}</Label>}
        <div className="flex gap-2">
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
              if (e.key === "Escape") setCreating(false);
            }}
            placeholder="Type a new industry"
            data-testid={`${testid}-new-input`}
            className={inputCls}
          />
          <button type="button" onClick={commit} data-testid={`${testid}-new-confirm`}
            className="shrink-0 rounded-md border border-blue-200 bg-blue-50 px-3 text-[11px] font-medium text-blue-700 hover:bg-blue-100">
            Add
          </button>
          <button type="button" onClick={() => setCreating(false)} aria-label="Cancel"
            className="shrink-0 rounded-md border border-slate-200 px-2 text-slate-400 hover:text-slate-600">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && <Label className="label-xs">{label}</Label>}
      <Select
        value={value ?? undefined}
        onValueChange={(v) => (v === "__create__" ? setCreating(true) : onChange(v))}
      >
        <SelectTrigger data-testid={testid} className={inputCls}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72 border-slate-200 bg-white text-slate-800">
          {value && !list.includes(value) && (
            <SelectItem value={value} className="text-sm">{value}</SelectItem>
          )}
          {options.map((o) => {
            const val = typeof o === "string" ? o : o.value;
            const lab = typeof o === "string" ? o : o.label;
            return <SelectItem key={val} value={val} className="text-sm">{lab}</SelectItem>;
          })}
          <SelectItem value="__create__" className="text-sm text-blue-700">+ {addLabel}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};

export const FormGrid = ({ children, cols = 2 }) => (
  <div className={cn("grid gap-4", cols === 2 ? "sm:grid-cols-2" : cols === 3 ? "sm:grid-cols-3" : "")}>
    {children}
  </div>
);

export const SectionTitle = ({ children }) => (
  <p className="mt-2 border-b border-slate-200 pb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
    {children}
  </p>
);
